import React, { useState } from "react";
import * as yup from "yup";
import { InferType } from "yup";
import {
  Input,
  Form,
  Formik,
  InputProps,
  ToggleSwitch,
  Select
} from "collections";
import i18n, { useTranslation } from "utilities/i18n";
import { Link, RouteComponentProps } from "polyrouter";
import {
  useCreateTeamsWebhookForTenantMutation,
  Rate,
  useUpdateTeamsActionConfigurationMutation,
  useNcIntegrationsQuery,
  NcIntegrationActionConfig,
  useTeamsHooksForTenantQuery
} from "generated/graphql";
import { usePolyPlusEnabled } from "utilities/polyPlus";
import { NOTIFICATION_ACTION_TYPES } from "Constants";
import { useHistory } from "react-router";
import { useTestFormBehavior } from "../Edit/TestForm";
import { SubmitButton } from "elements";
import { getDividerIndex, getFrequencyOptions } from "../../Helpers";
import { TestConnectionButton } from "elements/TestConnectionButton";

export const schema = yup.object({
  name: yup
    .string()
    .label(i18n.t("NAME"))
    .required(),
  url: yup
    .string()
    .url()
    .label(i18n.t("URL"))
    .required(),
  actionTypes: yup.array().of(
    yup.object({
      enabled: yup.bool(),
      actionType: yup.object({ id: yup.string(), type: yup.string() }),
      rate: yup.string(),
      allowedRate: yup.array().of(yup.string())
    })
  )
});

export const InitialValuesInit = (actionConfigs: any) => {
  let configs = actionConfigs as NcIntegrationActionConfig[];

  if (configs == null) {
    configs = [];
  }
  return {
    name: "",
    url: "",
    actionTypes: configs.map((at: any) => ({
      enabled: false,
      actionType: at!.type,
      rate: Rate.Daily,
      allowedRate: [Rate.Daily]
    }))
  };
};

function useTeamsNotificationsConfiguration(id: string) {
  const { polyPlusEnabled, fetching: polyPlusFetching } = usePolyPlusEnabled(
    id
  );
  const { fetching, ...rest } = useTeamsHooksForTenantQuery({
    variables: {
      id
    },
    pause: !polyPlusEnabled ?? true
  });
  return {
    fetching: fetching || polyPlusFetching,
    ...rest
  };
}

export type CreateType = InferType<typeof schema>;

export const FormInput: React.FC<InputProps> = ({
  name,
  label,
  isValid,
  ...rest
}) => {
  return (
    <div
      className={`form-group${
        isValid !== undefined && !isValid ? " error-message-spacing" : ""
      }`}
    >
      <label className="col-3 text-right" htmlFor={name + "-input"}>
        {label}:
      </label>
      <div className="col-5">
        <Input {...rest} name={name} data-testid={name} />
      </div>
    </div>
  );
};

interface SubmitFormValues {
  name: string;
  url: string;
  actionTypes: any[];
}

const CreateTeamsHook: React.FC<RouteComponentProps<{
  id: string;
}>> = ({
  match: {
    params: { id }
  }
}) => {
  const [showTestSpinner, setShowTestSpinnerValue] = useState(false);
  const { t } = useTranslation();
  const h = useHistory();
  const [, create] = useCreateTeamsWebhookForTenantMutation();
  const [, update] = useUpdateTeamsActionConfigurationMutation();
  const { data } = useTeamsNotificationsConfiguration(id);
  const hooks = data?.tenant.teamsWebhooks ?? [];
  const test = useTestFormBehavior();
  const { data: integrationData, fetching } = useNcIntegrationsQuery({
    variables: {
      input: {
        name: {
          eq: "teams"
        }
      }
    }
  });
  const actionConfigurations =
    integrationData?.notificationCenter?.integrations[0].actionConfigurations;

  if (fetching) {
    return null;
  }

  const initialValues = InitialValuesInit(actionConfigurations ?? []);

  const onSubmit = async ({ name, url, actionTypes }: SubmitFormValues) => {
    const created = await create({
      tenantId: id,
      input: {
        enabled: true,
        name,
        url
      }
    });
    const actions =
      created.data?.ncCreateTeamsWebhookForTenant?.actionConfigurations.map(
        async c => {
          const match = actionTypes.find(
            at => at.actionType.id === c.actionType.id
          );
          if (!match) return Promise.resolve();
          return update({
            input: {
              actionId: c.id,
              rate: match.rate as Rate,
              enabled: match.enabled
            }
          });
        }
      ) ?? [];
    await Promise.all(actions);
    h.goBack();
  };

  const checkDuplicates = (values: CreateType) => {
    let errors: any = {};
    const foundHook = !!hooks.find(
      x => x.name.toUpperCase() === values.name.toUpperCase()
    );

    if (foundHook) {
      errors["name"] = t("ERROR_DUPLICATE_INTEGRATION_HOOK");
    }

    return errors;
  };

  return (
    <Formik<CreateType>
      name="CreateTeamsHook"
      validateOnMount
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
      validate={checkDuplicates}
    >
      {({ isSubmitting, isValid, field, values }) => (
        <Form className="form grid">
          <div className="mt-15 ph-2" data-testid="create-teams-hook-header">
            <label>{t("NOTIFICATION_TEAMS_NEW")}</label>
          </div>
          {/* Error present is only relative on this property since it is on top and cuts off the input below it */}
          <FormInput
            {...field.name}
            isValid={isValid}
            label={field.name.label}
          />
          <FormInput {...field.url} label={field.url.label} />
          <div className="mt-15 ph-2">
            <label>{t("NOTIFICATION_TYPES")}</label>
          </div>
          {values.actionTypes.map((value, index) => {
            const actionType = value.actionType;
            const atField = field.actionTypes.get(index);

            const configData = actionConfigurations?.find(
              ac => ac?.type.type === actionType.type
            );

            const frequencyOptions = getFrequencyOptions(configData!);
            const dividerIndex = getDividerIndex(configData!.allowedRates);
            return (
              <div className="form-group" key={value.actionType.id}>
                <label
                  className="col-3 text-right"
                  htmlFor={atField.enabled.name}
                >
                  {t(
                    NOTIFICATION_ACTION_TYPES[actionType.type] ??
                      actionType.type
                  )}
                  :
                </label>
                <div className="col-1">
                  <ToggleSwitch
                    {...atField.enabled}
                    disabled={isSubmitting}
                    id={atField.enabled.id}
                  />
                </div>
                {value.enabled && (
                  <div className="col-3 col-xl-2">
                    <Select
                      placeholder="Frequency"
                      {...atField.rate}
                      options={frequencyOptions}
                      dividerIndices={dividerIndex ? [dividerIndex] : []}
                    />
                  </div>
                )}
              </div>
            );
          })}
          <div className="btn-container mb-1 ">
            <Link
              type="btn"
              className="btn"
              data-testid="cancel"
              to={"../teams"}
              replace
            >
              {t("CANCEL")}
            </Link>
            <TestConnectionButton
              onClick={async () => {
                setShowTestSpinnerValue(true);
                await test({
                  url: values.url
                });
                setShowTestSpinnerValue(false);
                return;
              }}
              isSubmitting={isSubmitting}
              isValid={isValid}
              showTestSpinner={showTestSpinner}
            />
            <SubmitButton
              isSubmitting={isSubmitting}
              disabled={!isValid || showTestSpinner}
              labelKey="NOTIFICATION_TEAMS_CREATE"
              submittingLabelKey="SAVING"
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default CreateTeamsHook;
